// The All accounts "Sort by" dropdown (go-live): Rank, Employee count, Fit, Engagement, Usage.
//
// It is the column headers' sort state seen from a dropdown, not a second sort: picking an option
// sets the same `SortState` a header click does, and a header click shows in the dropdown (or as
// "Column: <name>" when the header is not one of the options). Rank is 2 × Fit + 1 × Engagement
// over the rows shown (`rankByScore`), and the default once scores show.
//
// Employee count is the FTE column (`EMPLOYEE_COUNT_COLUMN`), so the dropdown and the header share it.
//
// Pure, and imported by `node --test`.

import type { BookAccount } from '../api/types';
import type { BookColumn } from './columns';
import type { SortState } from './sort';
import { formatHeadcount } from '../lib/format.ts';

export interface SortOption {
  key: string;
  label: string;
  // Rank, Fit and Engagement exist only while the score columns are drawn.
  needs_scores: boolean;
}

export const SORT_OPTIONS: SortOption[] = [
  { key: 'rank', label: 'Rank', needs_scores: true },
  { key: 'fte', label: 'Employee count', needs_scores: false },
  { key: 'fit_score', label: 'Fit', needs_scores: true },
  { key: 'engagement_score', label: 'Engagement', needs_scores: true },
  { key: 'credits_30', label: 'Usage', needs_scores: false },
];

export function sortOptionsFor(scoresShown: boolean): SortOption[] {
  return SORT_OPTIONS.filter(option => scoresShown || !option.needs_scores);
}

// Picking an option sorts the way a first header click would: Rank 1 first, the rest largest first.
export function sortForOption(key: string): SortState {
  return { key, direction: key === 'rank' ? 1 : -1 };
}

// The dropdown's value for a sort: the option's key, or '' when no option names it.
export function selectedSortKey(sort: SortState, scoresShown: boolean): string {
  const option = sortOptionsFor(scoresShown).find(candidate => candidate.key === sort.key);
  return option ? option.key : '';
}

// What the dropdown reads when no option names the sort: the header's name, else the default order.
export function unlistedSortLabel(sort: SortState, columns: BookColumn[]): string {
  if (!sort.key) return 'Default order';
  const column = columns.find(candidate => candidate.key === sort.key);
  return `Column: ${column?.label || sort.key}`;
}

export function employeeCountOf(row: BookAccount): number | null {
  return row.employee_count ?? row.clay?.engine?.employees ?? null;
}

// The FTE column, and the sort behind "Employee count". Its key is not `employee_count` because the
// shared drop order (`columnFit.ts`) would hide that one early; routing needs FTE to stay. Largest first,
// unknowns last.
export const EMPLOYEE_COUNT_COLUMN: BookColumn = {
  key: 'fte',
  label: 'FTE',
  numeric: true,
  width: '72px',
  hint: "Employees: the Salesforce count, else the Clay engine's. Largest first; blank when neither is known.",
  sortValue: row => employeeCountOf(row),
  accessor: row => String(employeeCountOf(row) ?? ''),
  cell: row => formatHeadcount(employeeCountOf(row)),
};
